/**
 * TScoreProfileChart — horizontal "skyline" of every scale's T-score, grouped
 * by category (validity / clinical / treatment / interpersonal).
 *
 * Reference guide-lines are drawn at T=50 (midline), 60 (mild), 70 (elevated),
 * 80 (marked). Bars at or above the "elevated" threshold (default 70) are
 * highlighted. Recharts via the ui/chart ChartContainer wrapper.
 */

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ReferenceLine,
  LabelList,
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import {
  CATEGORY_ORDER,
  categoryLabel,
  DEFAULT_THRESHOLDS,
  type ProfileThresholds,
  type ScaleCategory,
} from './types';

/** Minimal row shape this chart understands (profile + simulation both fit). */
export interface TScoreRow {
  scale_code: string;
  scale_name?: string;
  category?: string;
  t_score: number;
  elevated?: boolean;
}

interface TScoreProfileChartProps {
  scales: TScoreRow[];
  thresholds?: ProfileThresholds;
}

const NORMAL_FILL = '#3b82f6'; // blue
const ELEVATED_FILL = '#ef4444'; // red (t >= elevated threshold)

// A comfortable T-score axis window. T-scores are ~mean 50, sd 10.
const AXIS_MIN = 30;
const AXIS_MAX = 100;

export const TScoreProfileChart: React.FC<TScoreProfileChartProps> = ({
  scales,
  thresholds = DEFAULT_THRESHOLDS,
}) => {
  const grouped = useMemo(() => {
    const byCat = new Map<string, TScoreRow[]>();
    for (const s of scales) {
      const cat = (s.category ?? 'clinical') as ScaleCategory;
      const list = byCat.get(cat) ?? [];
      list.push(s);
      byCat.set(cat, list);
    }
    // Order known categories first, then any extras.
    const cats = [
      ...CATEGORY_ORDER.filter((c) => byCat.has(c)),
      ...Array.from(byCat.keys()).filter(
        (c) => !CATEGORY_ORDER.includes(c as ScaleCategory),
      ),
    ];
    return cats.map((cat) => ({
      category: cat,
      rows: [...(byCat.get(cat) ?? [])].sort((a, b) =>
        a.scale_code.localeCompare(b.scale_code),
      ),
    }));
  }, [scales]);

  if (!scales.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No scores to display yet.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map(({ category, rows }) => {
        const data = rows.map((r) => ({
          name: r.scale_name || r.scale_code,
          code: r.scale_code,
          t: Math.round(r.t_score),
          isElevated: r.elevated ?? r.t_score >= thresholds.elevated,
        }));
        // Each bar gets a fixed slice of vertical space; chart height scales
        // with the number of scales in the category (horizontal layout).
        const height = Math.max(120, data.length * 40 + 40);

        return (
          <section key={category} aria-label={`${categoryLabel(category)} scales`}>
            <h4 className="mb-2 text-sm font-semibold text-foreground">
              {categoryLabel(category)}
            </h4>
            <ChartContainer
              config={{ t: { label: 'T-score', color: NORMAL_FILL } }}
              className="aspect-auto w-full"
              style={{ height }}
            >
              <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 4, right: 40, bottom: 4, left: 8 }}
              >
                <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  domain={[AXIS_MIN, AXIS_MAX]}
                  ticks={[
                    thresholds.midline,
                    thresholds.mild,
                    thresholds.elevated,
                    thresholds.marked,
                  ]}
                  tickLine={false}
                  fontSize={11}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                />
                <ChartTooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  content={<ChartTooltipContent />}
                />
                {/* Reference guide-lines */}
                <ReferenceLine
                  x={thresholds.midline}
                  stroke="#9ca3af"
                  strokeDasharray="4 4"
                  label={{ value: `Midline ${thresholds.midline}`, position: 'top', fontSize: 10, fill: '#6b7280' }}
                />
                <ReferenceLine x={thresholds.mild} stroke="#f59e0b" strokeDasharray="2 4" />
                <ReferenceLine
                  x={thresholds.elevated}
                  stroke="#ef4444"
                  strokeDasharray="2 4"
                  label={{ value: `Elevated ${thresholds.elevated}`, position: 'top', fontSize: 10, fill: '#b91c1c' }}
                />
                <ReferenceLine x={thresholds.marked} stroke="#b91c1c" strokeDasharray="2 2" />
                <Bar dataKey="t" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  {data.map((d) => (
                    <Cell key={d.code} fill={d.isElevated ? ELEVATED_FILL : NORMAL_FILL} />
                  ))}
                  <LabelList dataKey="t" position="right" fontSize={11} />
                </Bar>
              </BarChart>
            </ChartContainer>
          </section>
        );
      })}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: NORMAL_FILL }} />
          Below threshold
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: ELEVATED_FILL }} />
          T &ge; {thresholds.elevated} (elevated)
        </span>
        <span>Guides at T = {thresholds.midline}/{thresholds.mild}/{thresholds.elevated}/{thresholds.marked}</span>
      </div>
    </div>
  );
};
